import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  Checkbox,
  Switch,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BoltIcon from '@mui/icons-material/Bolt';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';
import {
  useSalesLocations,
  useCreateSalesLocation,
  useUpdateSalesLocation,
  useDeleteSalesLocation,
  useAddSalesLocationProduct,
  useUpdateSalesLocationProduct,
  useDeleteSalesLocationProduct,
  useQuoteProducts,
  useSalesLocationsReport,
  useCustomerPrices,
  useUpsertCustomerPriceBulk,
  useDeleteCustomerPriceBulk,
  useMergeSalesLocation,
} from '../../hooks/useApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { SalesLocation, SalesLocationInput, SalesLocationProduct, SalesLocationProductInput } from '../../types';
import PlacesAutocomplete, { type PlaceResult } from '../../components/common/PlacesAutocomplete';
import PriceGroupsPage from './PriceGroupsPage';

const LOCATION_TYPES: Array<{ value: string; label: string }> = [
  { value: 'GENERAL', label: 'Generel' },
  { value: 'GRAVEL_PIT', label: 'Grusgrav' },
  { value: 'WAREHOUSE', label: 'Lager' },
  { value: 'QUARRY', label: 'Stenbrud' },
  { value: 'DEPOT', label: 'Depot' },
];

function formatDkk(n: number | null): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 2 }).format(n);
}

export default function ProductSalesPage() {
  const { data: locations = [], isLoading } = useSalesLocations();
  const createLocation = useCreateSalesLocation();
  const updateLocation = useUpdateSalesLocation();
  const deleteLocation = useDeleteSalesLocation();
  const { data: productsData } = useQuoteProducts();
  const products = productsData?.products || [];

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const [activeTab, setActiveTab] = useState<0 | 1 | 2 | 3>(0);
  const [editLocation, setEditLocation] = useState<Partial<SalesLocation> | null>(null); // null = closed, {} = new
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const selectedLocation = locations.find(l => l.id === selectedLocationId) || null;

  async function saveLocation() {
    if (!editLocation || !editLocation.name?.trim()) {
      setSnackbar({ open: true, message: 'Navn er påkrævet', severity: 'error' });
      return;
    }
    const payload: SalesLocationInput = {
      name: editLocation.name.trim(),
      type: editLocation.type || 'GENERAL',
      address: editLocation.address || null,
      postcode: editLocation.postcode || null,
      city: editLocation.city || null,
      country: editLocation.country || 'DK',
      placeName: editLocation.placeName || null,
      lat: editLocation.lat ?? null,
      lon: editLocation.lon ?? null,
      active: editLocation.active !== false,
      note: editLocation.note || null,
    };
    try {
      if (editLocation.id) {
        await updateLocation.mutateAsync({ id: editLocation.id, data: payload });
        setSnackbar({ open: true, message: 'Lokation opdateret', severity: 'success' });
      } else {
        const created = await createLocation.mutateAsync(payload);
        setSelectedLocationId(created.id);
        setSnackbar({ open: true, message: 'Lokation oprettet', severity: 'success' });
      }
      setEditLocation(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke gemme';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  }

  async function handleDeleteLocation(loc: SalesLocation) {
    if (!confirm(`Slet lokation "${loc.name}" og alle ${loc.products.length} varer tilknyttet?`)) return;
    try {
      await deleteLocation.mutateAsync(loc.id);
      if (selectedLocationId === loc.id) setSelectedLocationId(null);
      setSnackbar({ open: true, message: 'Lokation slettet', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke slette', severity: 'error' });
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            <StorefrontIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 28 }} />
            Produktsalg
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lokationer (grusgrave, lagre m.v.) med egne priser på DORA-varer.
          </Typography>
        </Box>
        {activeTab === 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditLocation({ active: true, country: 'DK', type: 'GENERAL' })}>
            Ny lokation
          </Button>
        )}

      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v as 0 | 1 | 2 | 3)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Lokationer" />
        <Tab label="Kundepriser" />
        <Tab label="Rapport" />
        <Tab label="Prisgrupper" />
      </Tabs>

      {activeTab === 3 ? (
        <PriceGroupsPage />
      ) : activeTab === 2 ? (
        <ReportPanel />
      ) : activeTab === 1 ? (
        <CustomerPricesPanel />
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
      ) : locations.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Ingen lokationer endnu. Opret din første for at komme i gang.
          </Typography>
        </Card>
      ) : (
        <CompactLocationsLayout
          locations={locations}
          selectedLocationId={selectedLocationId}
          onSelect={setSelectedLocationId}
          onEdit={setEditLocation}
          onDelete={handleDeleteLocation}
          selectedLocation={selectedLocation}
          products={products}
          setSnackbar={setSnackbar}
        />
      )}

      {/* Create/edit location dialog */}
      <Dialog open={editLocation != null} onClose={() => setEditLocation(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editLocation?.id ? 'Rediger lokation' : 'Ny lokation'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Navn *"
              value={editLocation?.name || ''}
              onChange={e => setEditLocation(l => ({ ...l!, name: e.target.value }))}
              fullWidth size="small"
            />
            <FormControl size="small" fullWidth>
              <Select
                value={editLocation?.type || 'GENERAL'}
                onChange={e => setEditLocation(l => ({ ...l!, type: e.target.value }))}
              >
                {LOCATION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>

            <PlacesAutocomplete
              label="Søg adresse"
              placeholder="fx Slotsvej 1, Aarhus"
              onChange={(place: PlaceResult | null) => {
                if (!place) return;
                setEditLocation(l => ({
                  ...l!,
                  address: place.address,
                  postcode: place.postcode,
                  city: place.city,
                  country: place.country || 'DK',
                  lat: place.lat ?? null,
                  lon: place.lon ?? null,
                  placeName: place.formatted?.split(',')[0] || l?.placeName,
                }));
              }}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Stedsnavn" sx={{ flex: 2 }} size="small"
                value={editLocation?.placeName || ''}
                onChange={e => setEditLocation(l => ({ ...l!, placeName: e.target.value }))} />
              <TextField label="Land" sx={{ width: 80 }} size="small"
                value={editLocation?.country || 'DK'}
                onChange={e => setEditLocation(l => ({ ...l!, country: e.target.value }))} />
            </Box>
            <TextField label="Adresse" size="small" fullWidth
              value={editLocation?.address || ''}
              onChange={e => setEditLocation(l => ({ ...l!, address: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Postnr" sx={{ width: 100 }} size="small"
                value={editLocation?.postcode || ''}
                onChange={e => setEditLocation(l => ({ ...l!, postcode: e.target.value }))} />
              <TextField label="By" sx={{ flex: 1 }} size="small"
                value={editLocation?.city || ''}
                onChange={e => setEditLocation(l => ({ ...l!, city: e.target.value }))} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField label="Latitude" sx={{ flex: 1 }} size="small" type="number"
                value={editLocation?.lat ?? ''}
                onChange={e => setEditLocation(l => ({ ...l!, lat: e.target.value ? parseFloat(e.target.value) : null }))} />
              <TextField label="Longitude" sx={{ flex: 1 }} size="small" type="number"
                value={editLocation?.lon ?? ''}
                onChange={e => setEditLocation(l => ({ ...l!, lon: e.target.value ? parseFloat(e.target.value) : null }))} />
              <Tooltip title="Bruges af chauffør-appen til at finde nærmeste lokation via GPS">
                <PlaceIcon color="action" fontSize="small" />
              </Tooltip>
            </Box>

            <TextField label="Note" multiline minRows={2} size="small" fullWidth
              value={editLocation?.note || ''}
              onChange={e => setEditLocation(l => ({ ...l!, note: e.target.value }))} />

            <FormControlLabel
              control={<Switch checked={editLocation?.active !== false}
                onChange={(_, checked) => setEditLocation(l => ({ ...l!, active: checked }))} />}
              label="Aktiv"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLocation(null)}>Annuller</Button>
          <Button variant="contained" onClick={saveLocation}
            disabled={createLocation.isPending || updateLocation.isPending}>
            {(createLocation.isPending || updateLocation.isPending) ? <CircularProgress size={18} /> : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// --- Products panel ---

interface DoraProduct {
  id: string;
  productNumber: string;
  name: string;
  salesPrice: string | null;
}

function LocationProductsPanel({
  location, products, onSnackbar,
}: {
  location: SalesLocation;
  products: DoraProduct[];
  onSnackbar: (s: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
}) {
  const addProduct = useAddSalesLocationProduct();
  const updateProduct = useUpdateSalesLocationProduct();
  const deleteProduct = useDeleteSalesLocationProduct();

  const [editProduct, setEditProduct] = useState<Partial<SalesLocationProduct> | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [mergeProductSource, setMergeProductSource] = useState<SalesLocationProduct | null>(null);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return location.products;
    return location.products.filter(p =>
      p.doraProductName.toLowerCase().includes(q) ||
      (p.doraProductNumber || '').toLowerCase().includes(q) ||
      (p.unitName || '').toLowerCase().includes(q)
    );
  }, [location.products, productSearch]);

  async function saveProduct() {
    // Kun KRÆV doraProductId når man opretter en helt ny vare. Eksisterende varer
    // kan være placeholders (importeret uden DORA-mapping) og skal stadig kunne
    // få priser opdateret. doraProductName skal være sat i alle tilfælde.
    const isCreate = !editProduct?.id;
    if (isCreate && !editProduct?.doraProductId) {
      onSnackbar({ open: true, message: 'Vælg et produkt', severity: 'error' });
      return;
    }
    if (!editProduct?.doraProductName) {
      onSnackbar({ open: true, message: 'Varen mangler navn', severity: 'error' });
      return;
    }
    const payload: SalesLocationProductInput = {
      doraProductId: editProduct.doraProductId || null,
      doraProductNumber: editProduct.doraProductNumber || '',
      doraProductName: editProduct.doraProductName || '',
      price: editProduct.price ?? null,
      costPrice: editProduct.costPrice ?? null,
      unitName: editProduct.unitName || null,
      autoTriggerDoraProductId: editProduct.autoTriggerDoraProductId || null,
      autoTriggerProductNumber: editProduct.autoTriggerProductNumber || null,
      autoTriggerProductName: editProduct.autoTriggerProductName || null,
      autoTriggerFactor: editProduct.autoTriggerFactor ?? null,
      autoTriggerPrice: editProduct.autoTriggerPrice ?? null,
      autoTriggerCostPrice: editProduct.autoTriggerCostPrice ?? null,
      autoTriggerUnitName: editProduct.autoTriggerUnitName || null,
      active: editProduct.active !== false,
    };
    try {
      if (editProduct.id) {
        await updateProduct.mutateAsync({ locationId: location.id, productId: editProduct.id, data: payload });
        onSnackbar({ open: true, message: 'Vare opdateret', severity: 'success' });
      } else {
        await addProduct.mutateAsync({ locationId: location.id, data: payload });
        onSnackbar({ open: true, message: 'Vare tilføjet', severity: 'success' });
      }
      setEditProduct(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke gemme';
      onSnackbar({ open: true, message: msg, severity: 'error' });
    }
  }

  async function handleDeleteProduct(p: SalesLocationProduct) {
    if (!confirm(`Fjern "${p.doraProductName}" fra ${location.name}?`)) return;
    try {
      await deleteProduct.mutateAsync({ locationId: location.id, productId: p.id });
      onSnackbar({ open: true, message: 'Vare fjernet', severity: 'success' });
    } catch {
      onSnackbar({ open: true, message: 'Kunne ikke fjerne', severity: 'error' });
    }
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>{location.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {location.products.length} varer
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<AddIcon />} size="small"
            onClick={() => setEditProduct({ active: true, autoTriggerFactor: 1 })}>
            Tilføj vare
          </Button>
        </Box>
        {location.products.length > 0 && (
          <TextField
            size="small" fullWidth
            placeholder={`Søg blandt ${location.products.length} varer...`}
            value={productSearch}
            onChange={e => setProductSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ mb: 1 }}
          />
        )}

        {location.products.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Ingen varer endnu. Tilføj en vare og sæt prisen.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vare</TableCell>
                <TableCell align="right">Salgspris</TableCell>
                <TableCell align="right">Kostpris</TableCell>
                <TableCell align="right">DB</TableCell>
                <TableCell>Enhed</TableCell>
                <TableCell>Auto-udløser</TableCell>
                <TableCell align="right" sx={{ width: 80 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map(p => {
                const margin = (p.price != null && p.costPrice != null) ? p.price - p.costPrice : null;
                const marginPct = (margin != null && p.price && p.price > 0) ? (margin / p.price) * 100 : null;
                return (
                <TableRow key={p.id} sx={{ opacity: p.active ? 1 : 0.5 }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{p.doraProductName}</Typography>
                    <Typography variant="caption" color="text.secondary">Nr: {p.doraProductNumber}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatDkk(p.price)}</TableCell>
                  <TableCell align="right" sx={{ color: p.costPrice == null ? 'warning.main' : 'text.primary' }}>
                    {p.costPrice == null ? 'Mangler' : formatDkk(p.costPrice)}
                  </TableCell>
                  <TableCell align="right">
                    {margin != null ? (
                      <Tooltip title={marginPct != null ? `${marginPct.toFixed(1)}% dækningsgrad` : ''}>
                        <Typography variant="body2" fontWeight={500}
                          color={margin < 0 ? 'error.main' : margin === 0 ? 'text.secondary' : 'success.main'}>
                          {formatDkk(margin)}
                        </Typography>
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{p.unitName || '—'}</TableCell>
                  <TableCell>
                    {p.autoTriggerProductName ? (
                      <Tooltip title={`Udløser: ${p.autoTriggerProductName}, ${formatDkk(p.autoTriggerPrice)} × ${p.autoTriggerFactor ?? 1} pr. ${p.unitName || 'enhed'}${p.autoTriggerCostPrice != null ? ` (kost: ${formatDkk(p.autoTriggerCostPrice)})` : ''}`}>
                        <Chip icon={<BoltIcon sx={{ fontSize: 14 }} />} size="small" label={p.autoTriggerProductName} color="warning" variant="outlined" />
                      </Tooltip>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Rediger"><IconButton size="small" onClick={() => setEditProduct(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Merge ind i…"><IconButton size="small" onClick={() => setMergeProductSource(p)}><CallMergeIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Slet"><IconButton size="small" color="error" onClick={() => handleDeleteProduct(p)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Product edit dialog */}
      <Dialog open={editProduct != null} onClose={() => setEditProduct(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editProduct?.id ? 'Rediger vare' : 'Tilføj vare'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Product picker (DORA) — kun låst når der allerede er en mapping, så
                vi ikke ved et uheld bryder eksisterende DORA-link. Placeholders
                (importeret uden doraProductId) skal stadig kunne mappes herfra. */}
            <Autocomplete<DoraProduct>
              size="small"
              options={products}
              getOptionLabel={(o) => `${o.productNumber} — ${o.name}`}
              value={products.find(p => p.id === editProduct?.doraProductId) || null}
              onChange={(_, val) => setEditProduct(p => ({
                ...p!,
                doraProductId: val?.id || null,
                doraProductNumber: val?.productNumber || (p?.doraProductNumber || ''),
                doraProductName: val?.name || (p?.doraProductName || ''),
                // Pre-fill price from DORA default if user hasn't set one
                price: p?.price ?? (val?.salesPrice ? parseFloat(val.salesPrice) : null),
              }))}
              disabled={!!(editProduct?.id && editProduct?.doraProductId)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="DORA-produkt *"
                  helperText={editProduct?.id && !editProduct?.doraProductId
                    ? `Placeholder: ${editProduct.doraProductNumber || '?'} · ${editProduct.doraProductName}. Vælg DORA-produkt for at koble.`
                    : undefined}
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Salgspris (DKK)" type="number" size="small" sx={{ flex: 1 }}
                value={editProduct?.price ?? ''}
                onChange={e => setEditProduct(p => ({ ...p!, price: e.target.value ? parseFloat(e.target.value) : null }))} />
              <TextField label="Kostpris (DKK)" type="number" size="small" sx={{ flex: 1 }}
                helperText="Intern kost pr. enhed — bruges til fortjeneste"
                value={editProduct?.costPrice ?? ''}
                onChange={e => setEditProduct(p => ({ ...p!, costPrice: e.target.value ? parseFloat(e.target.value) : null }))} />
              <TextField label="Enhed" size="small" sx={{ flex: 1 }}
                placeholder="fx ton, m³, stk"
                value={editProduct?.unitName || ''}
                onChange={e => setEditProduct(p => ({ ...p!, unitName: e.target.value }))} />
            </Box>
            {editProduct?.price != null && editProduct?.costPrice != null && (
              <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'success.light' }}>
                <Typography variant="caption" color="text.secondary">
                  DB pr. enhed: <strong style={{ color: (editProduct.price - editProduct.costPrice) < 0 ? '#c62828' : '#2e7d32' }}>
                    {formatDkk(editProduct.price - editProduct.costPrice)}
                  </strong>
                  {editProduct.price > 0 && (
                    <> — dækningsgrad {(((editProduct.price - editProduct.costPrice) / editProduct.price) * 100).toFixed(1)}%</>
                  )}
                </Typography>
              </Box>
            )}

            <FormControlLabel
              control={<Checkbox checked={editProduct?.active !== false}
                onChange={(_, checked) => setEditProduct(p => ({ ...p!, active: checked }))} />}
              label="Aktiv"
            />

            <Divider>
              <Chip icon={<BoltIcon sx={{ fontSize: 14 }} />} label="Auto-udløser (valgfri)" size="small" />
            </Divider>
            <Typography variant="caption" color="text.secondary">
              Når denne vare tilføjes et tilbud, oprettes også en ekstra linje (fx råstofafgift 3,34 kr/ton).
              Lad feltet stå tomt hvis ingen trigger skal anvendes.
            </Typography>

            <Autocomplete<DoraProduct>
              size="small"
              options={products}
              getOptionLabel={(o) => `${o.productNumber} — ${o.name}`}
              value={products.find(p => p.id === editProduct?.autoTriggerDoraProductId) || null}
              onChange={(_, val) => setEditProduct(p => ({
                ...p!,
                autoTriggerDoraProductId: val?.id || null,
                autoTriggerProductNumber: val?.productNumber || null,
                autoTriggerProductName: val?.name || null,
              }))}
              renderInput={(params) => <TextField {...params} label="Trigger-produkt" />}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Faktor" type="number" size="small" sx={{ flex: 1 }}
                helperText="fx 1 = samme mængde som kilde"
                value={editProduct?.autoTriggerFactor ?? ''}
                onChange={e => setEditProduct(p => ({ ...p!, autoTriggerFactor: e.target.value ? parseFloat(e.target.value) : null }))} />
              <TextField label="Salgspris/enhed" type="number" size="small" sx={{ flex: 1 }}
                value={editProduct?.autoTriggerPrice ?? ''}
                onChange={e => setEditProduct(p => ({ ...p!, autoTriggerPrice: e.target.value ? parseFloat(e.target.value) : null }))} />
              <TextField label="Kostpris/enhed" type="number" size="small" sx={{ flex: 1 }}
                value={editProduct?.autoTriggerCostPrice ?? ''}
                onChange={e => setEditProduct(p => ({ ...p!, autoTriggerCostPrice: e.target.value ? parseFloat(e.target.value) : null }))} />
              <TextField label="Enhed" size="small" sx={{ flex: 1 }}
                value={editProduct?.autoTriggerUnitName || ''}
                onChange={e => setEditProduct(p => ({ ...p!, autoTriggerUnitName: e.target.value }))} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProduct(null)}>Annuller</Button>
          <Button variant="contained" onClick={saveProduct}
            disabled={addProduct.isPending || updateProduct.isPending}>
            {(addProduct.isPending || updateProduct.isPending) ? <CircularProgress size={18} /> : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      {mergeProductSource && (
        <MergeProductDialog
          location={location}
          source={mergeProductSource}
          onClose={() => setMergeProductSource(null)}
          onDone={(msg) => { setMergeProductSource(null); onSnackbar({ open: true, message: msg, severity: 'success' }); }}
        />
      )}
    </Card>
  );
}

// --- Report panel ---

function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function ReportPanel() {
  const initial = defaultDateRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { data, isLoading, error } = useSalesLocationsReport(from, to);

  function fmtDate(d: string) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField label="Fra" type="date" size="small" value={from}
            InputLabelProps={{ shrink: true }}
            onChange={e => setFrom(e.target.value)} sx={{ width: 170 }} />
          <TextField label="Til" type="date" size="small" value={to}
            InputLabelProps={{ shrink: true }}
            onChange={e => setTo(e.target.value)} sx={{ width: 170 }} />
          <Box sx={{ flex: 1 }} />
          {data && (
            <Typography variant="body2" color="text.secondary">
              Gennemsøgt {data.totalCargos} cargos — {data.cargoCount} matchede lokationer
            </Typography>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">Kunne ikke hente rapport</Alert>
      ) : !data || data.locations.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">Ingen aktive lokationer at rapportere på.</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {data.locations.map(loc => {
            const isOpen = expanded[loc.locationId] || false;
            return (
              <Card key={loc.locationId} variant="outlined">
                <CardActionArea onClick={() => setExpanded(e => ({ ...e, [loc.locationId]: !e[loc.locationId] }))}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>{loc.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[loc.postcode, loc.city].filter(Boolean).join(' ') || 'Ingen adresse'}
                      </Typography>
                    </Box>
                    <Chip label={`${loc.cargoCount} cargo${loc.cargoCount === 1 ? '' : 's'}`}
                      color={loc.cargoCount > 0 ? 'primary' : 'default'}
                      variant={loc.cargoCount > 0 ? 'filled' : 'outlined'} />
                    {loc.cargoCount > 0 && (isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                  </CardContent>
                </CardActionArea>
                {loc.cargoCount > 0 && (
                  <Collapse in={isOpen}>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Cargo-ref</TableCell>
                            <TableCell>Kunde</TableCell>
                            <TableCell>Dato</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Til</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loc.cargos.map(c => (
                            <TableRow key={c.id}>
                              <TableCell>{c.ref}</TableCell>
                              <TableCell>{c.customerNumber || '—'}</TableCell>
                              <TableCell>{fmtDate(c.firstStopDate)}</TableCell>
                              <TableCell>{c.type}</TableCell>
                              <TableCell>{c.lastStopCity || '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                )}
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// --- Customer Prices panel — kunde-vis layout ---

interface CustomerPriceRow {
  id: string;
  organizationId: string;
  salesLocationId: string;
  salesLocationName: string;
  doraProductId: string;
  doraProductNumber: string;
  doraProductName: string;
  customerNumber: string;
  price: number;
  unitName: string | null;
  sourceQuoteId: string | null;
  updatedAt: string;
}

function CustomerPricesPanel() {
  const { data: pricesRaw = [], isLoading } = useCustomerPrices();
  const prices = pricesRaw as CustomerPriceRow[];
  const upsertBulk = useUpsertCustomerPriceBulk();
  const deleteBulk = useDeleteCustomerPriceBulk();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [customerFilter, setCustomerFilter] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);

  // Group prices by customer + by vare
  type VareGroup = {
    doraProductId: string;
    doraProductNumber: string;
    doraProductName: string;
    unitName: string | null;
    locationPrices: Map<string, { id: string; price: number; locationName: string }>;
    uniformPrice: number | null;
    sourceQuoteId: string | null;
  };
  const customerData = useMemo(() => {
    const byCustomer = new Map<string, Map<string, VareGroup>>();
    for (const p of prices) {
      if (!byCustomer.has(p.customerNumber)) byCustomer.set(p.customerNumber, new Map());
      const vareMap = byCustomer.get(p.customerNumber)!;
      const key = p.doraProductId;
      if (!vareMap.has(key)) {
        vareMap.set(key, {
          doraProductId: p.doraProductId,
          doraProductNumber: p.doraProductNumber,
          doraProductName: p.doraProductName,
          unitName: p.unitName,
          locationPrices: new Map(),
          uniformPrice: null,
          sourceQuoteId: p.sourceQuoteId,
        });
      }
      const g = vareMap.get(key)!;
      g.locationPrices.set(p.salesLocationId, { id: p.id, price: p.price, locationName: p.salesLocationName });
    }
    // Compute uniformPrice per vare
    for (const vareMap of byCustomer.values()) {
      for (const g of vareMap.values()) {
        const distinctPrices = new Set([...g.locationPrices.values()].map(lp => lp.price));
        g.uniformPrice = distinctPrices.size === 1 ? [...distinctPrices][0] : null;
      }
    }
    return byCustomer;
  }, [prices]);

  const customerList = useMemo(() => {
    const arr = [...customerData.entries()].map(([num, vares]) => ({
      num,
      varesCount: vares.size,
      pricesCount: [...vares.values()].reduce((s, v) => s + v.locationPrices.size, 0),
    }));
    arr.sort((a, b) => a.num.localeCompare(b.num));
    if (customerFilter.trim()) {
      const q = customerFilter.toLowerCase();
      return arr.filter(c => c.num.toLowerCase().includes(q));
    }
    return arr;
  }, [customerData, customerFilter]);

  const selectedVares = selectedCustomer ? [...(customerData.get(selectedCustomer)?.values() ?? [])] : [];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 1.5 }}>
      {/* Left: customer list */}
      <Box>
        <TextField
          size="small" fullWidth
          placeholder={`Søg blandt ${customerData.size} kunder...`}
          value={customerFilter}
          onChange={e => setCustomerFilter(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Card variant="outlined" sx={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
          {isLoading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
          ) : customerList.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Ingen kunder med priser</Typography>
            </Box>
          ) : customerList.map(c => {
            const isSelected = selectedCustomer === c.num;
            return (
              <Box
                key={c.num}
                onClick={() => setSelectedCustomer(c.num)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider',
                  cursor: 'pointer',
                  bgcolor: isSelected ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                }}
              >
                <Typography variant="body2" fontWeight={isSelected ? 600 : 400} noWrap>
                  {c.num}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {c.varesCount} {c.varesCount === 1 ? 'vare' : 'varer'}
                </Typography>
              </Box>
            );
          })}
        </Card>
      </Box>

      {/* Right: selected customer's vare table */}
      <Box>
        {!selectedCustomer ? (
          <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Vælg en kunde for at se og redigere kundepriser.
            </Typography>
          </Card>
        ) : (
          <Card variant="outlined">
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Kunde {selectedCustomer}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedVares.length} varer · pris ens på alle lokationer hvis ikke andet er sat
              </Typography>
            </Box>
            {selectedVares.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Ingen priser</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vare</TableCell>
                    <TableCell align="right">Pris</TableCell>
                    <TableCell>Enhed</TableCell>
                    <TableCell>Lokationer</TableCell>
                    <TableCell align="right" sx={{ width: 60 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedVares.map(g => (
                    <CustomerVareRow
                      key={g.doraProductId}
                      customerNumber={selectedCustomer}
                      vare={g}
                      onSave={async (newPrice) => {
                        try {
                          const r = await upsertBulk.mutateAsync({
                            customerNumber: selectedCustomer,
                            doraProductId: g.doraProductId,
                            doraProductNumber: g.doraProductNumber,
                            doraProductName: g.doraProductName,
                            price: newPrice,
                            unitName: g.unitName,
                            allLocations: true,
                          });
                          setSnackbar({ open: true, message: `Pris opdateret på ${r.upserted} lokationer`, severity: 'success' });
                        } catch {
                          setSnackbar({ open: true, message: 'Kunne ikke gemme', severity: 'error' });
                        }
                      }}
                      onDelete={async () => {
                        if (!confirm(`Slet pris for ${g.doraProductName}?`)) return;
                        try {
                          await deleteBulk.mutateAsync({ customerNumber: selectedCustomer, doraProductId: g.doraProductId });
                          setSnackbar({ open: true, message: 'Slettet', severity: 'success' });
                        } catch {
                          setSnackbar({ open: true, message: 'Kunne ikke slette', severity: 'error' });
                        }
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        )}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function CustomerVareRow({
  customerNumber: _customerNumber, vare, onSave, onDelete,
}: {
  customerNumber: string;
  vare: {
    doraProductId: string;
    doraProductNumber: string;
    doraProductName: string;
    unitName: string | null;
    locationPrices: Map<string, { id: string; price: number; locationName: string }>;
    uniformPrice: number | null;
  };
  onSave: (price: number) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [editingPrice, setEditingPrice] = useState<string>(vare.uniformPrice != null ? String(vare.uniformPrice) : '');
  const [saving, setSaving] = useState(false);
  const isUniform = vare.uniformPrice != null;
  const locationsList = [...vare.locationPrices.values()];

  const dirty = parseFloat(editingPrice) !== vare.uniformPrice;

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{vare.doraProductName}</Typography>
        <Typography variant="caption" color="text.secondary">Nr {vare.doraProductNumber}</Typography>
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
          <TextField
            size="small" type="number"
            value={editingPrice}
            onChange={e => setEditingPrice(e.target.value)}
            inputProps={{ step: 0.01, style: { textAlign: 'right', padding: '4px 6px', fontSize: '0.85rem' } }}
            sx={{ width: 110 }}
          />
          <Button
            size="small"
            disabled={!dirty || !editingPrice || saving}
            onClick={async () => {
              setSaving(true);
              try { await onSave(parseFloat(editingPrice)); } finally { setSaving(false); }
            }}
            sx={{ minWidth: 0, px: 1 }}
          >
            {saving ? <CircularProgress size={14} /> : 'Gem'}
          </Button>
        </Box>
        {!isUniform && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
            Forskellige priser pr. lokation — Gem ensretter alle
          </Typography>
        )}
      </TableCell>
      <TableCell>{vare.unitName || '—'}</TableCell>
      <TableCell>
        <Tooltip title={locationsList.map(lp => `${lp.locationName}: ${lp.price.toFixed(2)} kr.`).join('\n')}>
          <Chip size="small" variant="outlined" label={`${locationsList.length} lok.`} />
        </Tooltip>
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" color="error" onClick={onDelete}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

// ============================================================
// Compact list + merge UI for lokationer
// ============================================================

interface CompactProps {
  locations: SalesLocation[];
  selectedLocationId: string | null;
  onSelect: (id: string) => void;
  onEdit: (loc: Partial<SalesLocation> & { id?: string }) => void;
  onDelete: (loc: SalesLocation) => void;
  selectedLocation: SalesLocation | null | undefined;
  products: { id: string; productNumber: string; name: string; salesPrice: string | null }[];
  setSnackbar: (s: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
}

function CompactLocationsLayout({
  locations, selectedLocationId, onSelect, onEdit, onDelete,
  selectedLocation, products, setSnackbar,
}: CompactProps) {
  const [search, setSearch] = useState('');
  const [mergeSource, setMergeSource] = useState<SalesLocation | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter(l =>
      l.name.toLowerCase().includes(q) ||
      (l.address || '').toLowerCase().includes(q) ||
      (l.postcode || '').toLowerCase().includes(q) ||
      (l.city || '').toLowerCase().includes(q)
    );
  }, [locations, search]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <TextField
          size="small"
          placeholder={`Søg blandt ${locations.length} lokationer...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ mb: 1 }}
        />
        <Card variant="outlined" sx={{ flex: 1, overflow: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
          <Box>
            {filtered.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Ingen match</Typography>
              </Box>
            ) : filtered.map(loc => {
              const isSelected = selectedLocationId === loc.id;
              return (
                <Box
                  key={loc.id}
                  onClick={() => onSelect(loc.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    px: 1, py: 0.5,
                    cursor: 'pointer',
                    borderBottom: 1, borderColor: 'divider',
                    backgroundColor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': { backgroundColor: isSelected ? 'action.selected' : 'action.hover' },
                    opacity: loc.active ? 1 : 0.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={isSelected ? 600 : 400} noWrap>
                      {loc.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.7rem' }}>
                      {loc.products.length} varer{loc.city ? ` · ${loc.city}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0 }}>
                    <Tooltip title="Rediger">
                      <IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(loc); }} sx={{ p: 0.25 }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Merge ind i…">
                      <IconButton size="small" onClick={e => { e.stopPropagation(); setMergeSource(loc); }} sx={{ p: 0.25 }}>
                        <CallMergeIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Slet">
                      <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(loc); }} sx={{ p: 0.25 }}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>
      </Box>

      <Box sx={{ minHeight: 0 }}>
        {selectedLocation ? (
          <LocationProductsPanel
            location={selectedLocation}
            products={products}
            onSnackbar={setSnackbar}
          />
        ) : (
          <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Vælg en lokation for at redigere varer og priser.
            </Typography>
          </Card>
        )}
      </Box>

      {mergeSource && (
        <MergeLocationDialog
          source={mergeSource}
          allLocations={locations}
          onClose={() => setMergeSource(null)}
          onDone={(msg) => { setMergeSource(null); setSnackbar({ open: true, message: msg, severity: 'success' }); }}
        />
      )}
    </Box>
  );
}

function MergeLocationDialog({
  source, allLocations, onClose, onDone,
}: {
  source: SalesLocation;
  allLocations: SalesLocation[];
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [target, setTarget] = useState<SalesLocation | null>(null);
  const merge = useMergeSalesLocation();
  const candidates = allLocations.filter(l => l.id !== source.id);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!target) return;
    setError(null);
    try {
      const result = await merge.mutateAsync({ sourceId: source.id, targetId: target.id });
      onDone(`Merget — ${result.movedProducts} varer flyttet.`);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Merge fejlede');
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Merge "{source.name}" ind i…</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="warning">
            Alle varer fra <strong>{source.name}</strong> flyttes som de er til mål-lokationen, og <strong>{source.name}</strong> slettes bagefter.
            Vare-dubletter på samme navn merges <strong>ikke</strong> — brug per-vare merge-knappen bagefter hvis de skal flettes. Kan ikke fortrydes.
          </Alert>
          <Autocomplete<SalesLocation>
            size="small"
            options={candidates}
            value={target}
            getOptionLabel={(o) => `${o.name} (${o.products.length} varer)`}
            onChange={(_, val) => setTarget(val)}
            renderInput={(params) => <TextField {...params} label="Mål-lokation" placeholder="Vælg…" />}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button variant="contained" color="warning" startIcon={<CallMergeIcon />}
          onClick={run} disabled={!target || merge.isPending}>
          {merge.isPending ? <CircularProgress size={16} /> : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================
// Hook + dialog for merging two products on a location
// ============================================================

function useMergeSalesLocationProductHook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locId, sourceId, targetId }: { locId: string; sourceId: string; targetId: string }) =>
      api.mergeSalesLocationProduct(locId, sourceId, targetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-locations'] });
      qc.invalidateQueries({ queryKey: ['price-groups'] });
    },
  });
}

function MergeProductDialog({
  location, source, onClose, onDone,
}: {
  location: SalesLocation;
  source: SalesLocationProduct;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [target, setTarget] = useState<SalesLocationProduct | null>(null);
  const merge = useMergeSalesLocationProductHook();
  const candidates = location.products.filter(p => p.id !== source.id);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!target) return;
    setError(null);
    try {
      const result = await merge.mutateAsync({ locId: location.id, sourceId: source.id, targetId: target.id });
      onDone(`Merget — ${result.moved} priser flyttet, ${result.mergedTie} kollisioner løst.`);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Merge fejlede');
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Merge "{source.doraProductName}" ind i…</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="warning">
            Alle prisgruppe-priser fra <strong>{source.doraProductName}</strong> flyttes til mål-varen.
            Ved kollision på samme prisgruppe vinder billigste pris, og kilde-varen slettes bagefter. Kan ikke fortrydes.
          </Alert>
          <Autocomplete<SalesLocationProduct>
            size="small"
            options={candidates}
            value={target}
            getOptionLabel={(o) => `${o.doraProductName}${o.doraProductNumber ? ' · ' + o.doraProductNumber : ''}`}
            onChange={(_, val) => setTarget(val)}
            renderInput={(params) => <TextField {...params} label="Mål-vare" placeholder="Vælg…" />}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button variant="contained" color="warning" startIcon={<CallMergeIcon />}
          onClick={run} disabled={!target || merge.isPending}>
          {merge.isPending ? <CircularProgress size={16} /> : 'Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { MergeProductDialog };
